import dayjs from 'dayjs';

/** A MyKad number has 12 digits, shown as `YYMMDD-PB-###G`. */
export const IC_NUMBER_DIGITS = 12;

/** The digits of what was typed, capped at the ID's length. */
export const icNumberDigits = (
  input: string,
  digits: number = IC_NUMBER_DIGITS
): string => input.replace(/\D/g, '').slice(0, digits);

/** What was typed, as `900101-14-5678`; dashes appear as the digits reach each group. */
export const formatIcNumber = (
  input: string,
  length: number = IC_NUMBER_DIGITS
): string => {
  const digits = icNumberDigits(input, length);
  return [digits.slice(0, 6), digits.slice(6, 8), digits.slice(8)]
    .filter(Boolean)
    .join('-');
};

export const isCompleteIcNumber = (
  input: string,
  digits: number = IC_NUMBER_DIGITS
): boolean => icNumberDigits(input, digits).length === digits;

/** A saved ID as the portal shows it: `•••• 5678`. */
export const maskedIcNumber = (last4: string): string => `•••• ${last4}`;

/**
 * Names for a short line: "A", "A and B", "A, B and 3 more". Shows at most two in full.
 */
export const shortNameList = (names: string[]): string => {
  if (names.length <= 2) return names.join(' and ');
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
};

/** A word's first letter or digit; the constructor keeps the Unicode class legal on ES2015. */
const FIRST_LETTER_OR_DIGIT = new RegExp('[\\p{L}\\p{N}]', 'u');

/**
 * "FA" for "Food Aid Foundation", "MS" for "Matcha @ SS21": the first letter or digit of the
 * first two words that have one.
 */
export const nameInitials = (name: string): string =>
  name
    .split(/\s+/)
    .map((word) => word.match(FIRST_LETTER_OR_DIGIT)?.[0])
    .filter((initial): initial is string => Boolean(initial))
    .slice(0, 2)
    .map((initial) => initial.toUpperCase())
    .join('');

/** "Jan to Oct 2026"; one month alone is "Oct 2026". */
export const formatDonationPeriod = (from: string, to: string): string => {
  const start = dayjs(from);
  const end = dayjs(to);
  if (start.isSame(end, 'month')) return end.format('MMM YYYY');
  const startFormat = start.isSame(end, 'year') ? 'MMM' : 'MMM YYYY';
  return `${start.format(startFormat)} to ${end.format('MMM YYYY')}`;
};

/** "October 2026" for `2026-10`. */
export const formatDonationMonth = (month: string): string =>
  dayjs(`${month}-01`).format('MMMM YYYY');

export const pluralize = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

export const taxStatusLabel = (taxDeductible: boolean) =>
  taxDeductible ? 'Tax-deductible' : 'Not tax-deductible';

/** The year My Donations opens on: the current one when it has donations, else the newest that does. */
export const defaultDonationYear = (
  years: number[],
  currentYear: number
): number | undefined => (years.includes(currentYear) ? currentYear : years[0]);
